/*
 * Corporate email signature template (hardcoded by design — Marketing owns the layout).
 * The generated HTML must stay email-client safe: nested tables, inline styles only,
 * explicit width/height attributes on images, bgcolor attributes on colored cells.
 * Do not reference webpack-hashed asset URLs here; only /images/signature/ public assets.
 */

const COLORS = {
  Navy: '#020F59',
  Dark: '#050C1A',
  Red: '#CF3239',
  White: '#FFFFFF',
};

const UTM = 'utm_medium=email&utm_source=signature';

// Each logo is a full-cell TILE (logo centered on a baked-in white background,
// 148x57 display @2x): dark-mode clients recolor CSS backgrounds but leave image
// pixels alone, so the white strip survives only as image content. Logo artwork is
// centered at the approved design's exact scale (FIRMA BACKGROUND placements x 640/1811).
const BRAND_LINKS = [
  {
    href: `https://staytuff.com/?${UTM}&utm_campaign=staytuff`,
    img: 'staytuff-tile.png',
    alt: 'Stay-Tuff',
  },
  {
    href: `https://www.designmasterfence.com/?${UTM}&utm_campaign=designmaster`,
    img: 'designmaster-tile.png',
    alt: 'Designmaster',
  },
  {
    href: `https://www.magnumfasteners.com/?${UTM}&utm_campaign=magnum`,
    img: 'magnum-tile.png',
    alt: 'Magnum',
  },
  {
    href: `https://www.rangemasterfence.com/?${UTM}&utm_campaign=rangemaster`,
    img: 'rangemaster-tile.png',
    alt: 'Rangemaster',
  },
];

// No www. — the www subdomain is missing from the mcswusa.com certificate
const LOCKUP_LINK = `https://mcswusa.com/?${UTM}&utm_campaign=mcswusa`;

const escapeHtml = (value) => {
  return String(value === null || value === undefined ? '' : value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
};

/**
 * Builds the corporate signature HTML. Rows 1 and 2 are server-composed images
 * (GetSignatureBanner / GetSignatureContactBar) — Outlook's Word renderer adds phantom
 * spacing around images butted against table cells and reformats text on paste, which
 * no HTML survives (see docs/*.msg test extractions); composed images cannot be
 * misrendered or retyped. Row 3 keeps individually linked brand logos.
 * @param {Object} data
 * @param {string} data.FullName       used for the banner alt text
 * @param {string} data.JobTitle       used for the banner alt text
 * @param {string} data.Email          featured email address (mailto link + alt, optional)
 * @param {string} data.MobilePhone    featured mobile number (alt text, optional)
 * @param {string} data.OfficePhone    featured office number (alt text, optional)
 * @param {string} data.BannerLeftUrl  absolute URL of the composed top band, left slice
 * @param {string} data.BannerRightUrl absolute URL of the composed top band, right slice
 * @param {string} data.EmailBarUrl    absolute URL of the email segment image
 * @param {string} data.PhoneBarUrl    absolute URL of the phone segment image
 * @param {string} data.LockupBarUrl   absolute URL of the lockup segment image
 * @param {string} data.AssetBaseUrl   absolute base URL ending in /, where images/signature/ lives
 */
export const buildSignatureHtml = (data) => {
  const assetBase = `${data.AssetBaseUrl}images/signature/`;

  // Edge spacers push the four logo tiles toward the center, matching the approved
  // design. Spacers are white IMAGES too — empty cells get their widths redistributed
  // by Word on paste, and painted cells get recolored by dark mode.
  const edgeSpacer = `<td width="24" style="width:24px;font-size:1px;"><img align="bottom" src="${assetBase}spacer.png" width="24" height="57" alt="" style="display:block;width:24px;height:57px;border:0;"></td>`;
  const brandCells = edgeSpacer + BRAND_LINKS.map((brand) => {
    return (
      `<td width="148" style="width:148px;padding:0;font-size:1px;">` +
      `<a href="${brand.href}" target="_blank" style="text-decoration:none;">` +
      `<img align="bottom" src="${assetBase}${brand.img}" width="148" height="57" alt="${brand.alt}" style="display:block;width:148px;height:57px;border:0;">` +
      `</a></td>`
    );
  }).join('') + edgeSpacer;

  const phonesAlt = [
    data.MobilePhone ? `Mobile: ${data.MobilePhone}` : '',
    data.OfficePhone ? `Office: ${data.OfficePhone}` : '',
  ].filter((line) => !!line).join(' | ');

  const telHref = (raw) => `tel:${String(raw || '').replace(/[^+\d]/g, '')}`;

  // Phone cell: with BOTH numbers, two half-height images stack, each with its own
  // tel: link; with one number, the full-height single-line image carries its link.
  const hasMobile = !!data.MobilePhone;
  const hasOffice = !!data.OfficePhone;
  let phoneCellContent;
  if (hasMobile && hasOffice) {
    phoneCellContent =
      `<a href="${telHref(data.MobilePhoneRaw)}" style="text-decoration:none;"><img align="bottom" src="${data.MobileBarUrl}" width="160" height="32" alt="Mobile: ${escapeHtml(data.MobilePhone)}" style="display:block;width:160px;height:32px;border:0;"></a>` +
      `<a href="${telHref(data.OfficePhoneRaw)}" style="text-decoration:none;"><img align="bottom" src="${data.OfficeBarUrl}" width="160" height="32" alt="Office: ${escapeHtml(data.OfficePhone)}" style="display:block;width:160px;height:32px;border:0;"></a>`;
  } else if (hasMobile || hasOffice) {
    const raw = hasMobile ? data.MobilePhoneRaw : data.OfficePhoneRaw;
    phoneCellContent =
      `<a href="${telHref(raw)}" style="text-decoration:none;"><img align="bottom" src="${data.PhoneBarUrl}" width="160" height="64" alt="${escapeHtml(phonesAlt)}" style="display:block;width:160px;height:64px;border:0;"></a>`;
  } else {
    phoneCellContent =
      `<img align="bottom" src="${data.PhoneBarUrl}" width="160" height="64" alt="" style="display:block;width:160px;height:64px;border:0;">`;
  }

  const parts = [
    // The outer table carries the dark background: clients (notably Word on send)
    // can inject spacing BETWEEN rows, which sits outside the painted cells — a
    // table-level background makes those gaps dark instead of page-white.
    `<table cellpadding="0" cellspacing="0" border="0" width="640" bgcolor="${COLORS.Dark}" style="border-collapse:collapse;width:640px;background-color:${COLORS.Dark};">`,
    // Row 1: the composed banner (photo + name + title + M logo)
    // The outer wrapper cells carry the SAME background as their band: clients that
    // scale the email (Gmail mobile fit-to-width, DPI zoom) produce sub-pixel rounding
    // seams between the stacked tables, and an unpainted wrapper shows them as white
    // hairlines. Painted, the seams are invisible.
    // THE GAP MECHANISM (test12-verified): Word wraps every img in <p class=MsoNormal>
    // (12pt), and standards-mode renderers (Gmail/new Outlook/mobile) reserve ~4px of
    // baseline descent per paragraph — the CSS "strut". Nothing that survives Word can
    // remove it (spans don't affect the strut; align/display/td font-size get stripped;
    // classic Outlook has no strut, which is why it always rendered clean). So each
    // image's cell is painted to match the band BELOW it, making the strut strip read
    // as that band starting a few px early. The banner is split at x=418 (the dark/red
    // boundary of row 2) precisely so its two halves can carry different below-paints.
    `<tr>`,
    `<td style="padding:0;font-size:1px;">`,
    `<table cellpadding="0" cellspacing="0" border="0" width="640" style="border-collapse:collapse;">`,
    `<tr>`,
    `<td bgcolor="${COLORS.Dark}" width="418" valign="top" style="background-color:${COLORS.Dark};padding:0;font-size:1px;">`,
    `<img align="bottom" src="${data.BannerLeftUrl}" width="418" height="151" alt="${escapeHtml(data.FullName)} — ${escapeHtml(data.JobTitle)}" style="display:block;width:418px;height:151px;border:0;">`,
    `</td>`,
    `<td bgcolor="${COLORS.Red}" width="222" valign="top" style="background-color:${COLORS.Red};padding:0;font-size:1px;">`,
    `<img align="bottom" src="${data.BannerRightUrl}" width="222" height="151" alt="" style="display:block;width:222px;height:151px;border:0;">`,
    `</td>`,
    `</tr>`,
    `</table>`,
    `</td>`,
    `</tr>`,
    // Row 2: three server-composed image segments (same rationale as the banner —
    // contact info as pixels can't be reformatted by clients or trivially retyped).
    // Each segment keeps its natural link: mailto on the email, the UTM link on the lockup.
    `<tr>`,
    `<td bgcolor="${COLORS.Dark}" style="background-color:${COLORS.Dark};padding:0;font-size:1px;">`,
    `<table cellpadding="0" cellspacing="0" border="0" width="640" bgcolor="${COLORS.Dark}" style="border-collapse:collapse;background-color:${COLORS.Dark};">`,
    `<tr>`,
    `<td bgcolor="${COLORS.Dark}" width="258" valign="top" style="background-color:${COLORS.Dark};padding:0;font-size:1px;">`,
    data.Email
      ? `<a href="mailto:${escapeHtml(data.Email)}" style="text-decoration:none;"><img align="bottom" src="${data.EmailBarUrl}" width="258" height="64" alt="Email: ${escapeHtml(data.Email)}" style="display:block;width:258px;height:64px;border:0;"></a>`
      : `<img align="bottom" src="${data.EmailBarUrl}" width="258" height="64" alt="" style="display:block;width:258px;height:64px;border:0;">`,
    `</td>`,
    `<td bgcolor="${COLORS.Dark}" width="160" valign="top" style="background-color:${COLORS.Dark};padding:0;font-size:1px;">`,
    phoneCellContent,
    `</td>`,
    `<td bgcolor="${COLORS.Red}" width="222" valign="top" style="background-color:${COLORS.Red};padding:0;font-size:1px;">`,
    `<a href="${LOCKUP_LINK}" target="_blank" style="text-decoration:none;">`,
    `<img align="bottom" src="${data.LockupBarUrl}" width="222" height="64" alt="Mid-Continent Steel &amp; Wire | Deacero" style="display:block;width:222px;height:64px;border:0;">`,
    `</a>`,
    `</td>`,
    `</tr>`,
    `</table>`,
    `</td>`,
    `</tr>`,
    // Row 3: brand logos (white strip)
    `<tr>`,
    `<td bgcolor="${COLORS.White}" style="background-color:${COLORS.White};padding:0;">`,
    `<table cellpadding="0" cellspacing="0" border="0" width="640" style="border-collapse:collapse;">`,
    `<tr>${brandCells}</tr>`,
    `</table>`,
    `</td>`,
    `</tr>`,
    `</table>`,
  ];

  return parts.join('');
};

export const buildSignaturePlainText = (data) => {
  const lines = [data.FullName, data.JobTitle];

  if (data.Email) {
    lines.push(`Email: ${data.Email}`);
  }

  if (data.MobilePhone) {
    lines.push(`Mobile: ${data.MobilePhone}`);
  }

  if (data.OfficePhone) {
    lines.push(`Office: ${data.OfficePhone}`);
  }

  lines.push('Mid-Continent Steel & Wire | Deacero');
  lines.push('https://mcswusa.com/');

  return lines.filter((line) => !!line).join('\n');
};
